/**
 * AnnouncementsButton
 * Bell icon in the header with unread badge + modal for viewing announcements.
 * Auto-opens on dashboard load when there are unread announcements (once per session).
 *
 * Layout:
 *   1. Pinned — tinted alert cards at top (always visible, can't be dismissed)
 *   2. Latest — featured card with collapsible body
 *   3. Earlier — accordion cards (multiple can be open)
 *
 * Design: follows OathUI patterns (rounded-xl cards on gray background,
 * consistent borders, TypeBadge pills like OathStatusBadge, primary accents).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  MegaphoneIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { OathModal, OathEmptyState, OathSpinner, OathButton } from './OathUI';
import { formatDate } from './OathTableComponents';
import {
  getAnnouncements,
  markAnnouncementRead,
  markAllAnnouncementsRead,
} from '../../services/oathGlobalMessageService';

// ============================================
// Constants
// ============================================

const AUTO_OPEN_SESSION_KEY = 'oath_announcements_auto_opened';
const POLL_INTERVAL = 60000; // 60s — surface new announcements without a reload

const typeConfig = {
  info: {
    icon: InformationCircleIcon,
    accent: 'bg-blue-500',
    iconColor: 'text-blue-600 dark:text-blue-400',
    badgeBg: 'bg-blue-100 dark:bg-blue-900/40',
    badgeText: 'text-blue-700 dark:text-blue-300',
    pinnedBg: 'bg-blue-50 dark:bg-blue-950/30',
    pinnedBorder: 'border-blue-200 dark:border-blue-800',
    label: 'Update',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    accent: 'bg-rose-500',
    iconColor: 'text-rose-600 dark:text-rose-400',
    badgeBg: 'bg-rose-100 dark:bg-rose-900/40',
    badgeText: 'text-rose-700 dark:text-rose-300',
    pinnedBg: 'bg-rose-50 dark:bg-rose-950/30',
    pinnedBorder: 'border-rose-200 dark:border-rose-800',
    label: 'Action Required',
  },
  feature: {
    icon: SparklesIcon,
    accent: 'bg-violet-500',
    iconColor: 'text-violet-600 dark:text-violet-400',
    badgeBg: 'bg-violet-100 dark:bg-violet-900/40',
    badgeText: 'text-violet-700 dark:text-violet-300',
    pinnedBg: 'bg-violet-50 dark:bg-violet-950/30',
    pinnedBorder: 'border-violet-200 dark:border-violet-800',
    label: "What's New",
  },
  maintenance: {
    icon: WrenchScrewdriverIcon,
    accent: 'bg-slate-400',
    iconColor: 'text-slate-500 dark:text-slate-400',
    badgeBg: 'bg-slate-100 dark:bg-slate-700/40',
    badgeText: 'text-slate-600 dark:text-slate-300',
    pinnedBg: 'bg-slate-50 dark:bg-slate-800/30',
    pinnedBorder: 'border-slate-200 dark:border-slate-700',
    label: 'System Notice',
  },
};

// ============================================
// Helpers
// ============================================

const formatRelativeDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

// Rich-content styles — matches OathUI text patterns
const richContentClasses = `text-sm leading-relaxed text-gray-700 dark:text-gray-300
  [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-gray-900 [&_h1]:dark:text-white
  [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-1.5 [&_h2]:text-gray-900 [&_h2]:dark:text-white
  [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-gray-900 [&_h3]:dark:text-white
  [&_p]:mb-2.5 [&_p]:leading-relaxed
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2.5 [&_ul]:space-y-1
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2.5 [&_ol]:space-y-1
  [&_li]:text-sm [&_li]:leading-relaxed
  [&_a]:text-primary [&_a]:dark:text-primary-light [&_a]:underline [&_a]:hover:opacity-80
  [&_strong]:font-semibold [&_strong]:text-gray-900 [&_strong]:dark:text-white [&_em]:italic
  [&_img]:rounded-lg [&_img]:max-w-full [&_img]:my-2
  [&_iframe]:rounded-lg [&_iframe]:max-w-full [&_iframe]:my-2 [&_iframe]:aspect-video [&_iframe]:w-full
  [&_blockquote]:border-l-4 [&_blockquote]:border-primary/30 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-gray-500 [&_blockquote]:dark:text-gray-400 [&_blockquote]:my-2
  [&_code]:bg-gray-100 [&_code]:dark:bg-gray-700 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono
  [&_hr]:border-gray-200 [&_hr]:dark:border-gray-700 [&_hr]:my-3
  [&_table]:w-full [&_table]:text-sm [&_table]:my-2
  [&_th]:text-left [&_th]:font-semibold [&_th]:pb-2 [&_th]:border-b [&_th]:border-gray-200 [&_th]:dark:border-gray-700
  [&_td]:py-1.5 [&_td]:border-b [&_td]:border-gray-100 [&_td]:dark:border-gray-800`;

// ============================================
// Animated collapsible wrapper
// ============================================

const Collapsible = ({ open, children }) => (
  <div
    className="grid transition-[grid-template-rows] duration-200 ease-out"
    style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
  >
    <div className="overflow-hidden">{children}</div>
  </div>
);

// Scroll item into view when expanded (after animation starts)
const useScrollOnExpand = (isExpanded) => {
  const ref = useRef(null);
  const prevExpanded = useRef(isExpanded);
  useEffect(() => {
    if (isExpanded && !prevExpanded.current && ref.current) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 220);
    }
    prevExpanded.current = isExpanded;
  }, [isExpanded]);
  return ref;
};

// ============================================
// TypeBadge — pill badge matching OathStatusBadge pattern
// ============================================

const TypeBadge = ({ config }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${config.badgeBg} ${config.badgeText}`}>
    {config.label}
  </span>
);

// ============================================
// PinnedBanner — tinted card with type color background
// ============================================

const PinnedBanner = ({ announcement, isExpanded, onToggle }) => {
  const config = typeConfig[announcement.messageType] || typeConfig.info;
  const TypeIcon = config.icon;
  const scrollRef = useScrollOnExpand(isExpanded);

  return (
    <div ref={scrollRef} className="mx-4 mb-2">
      <div className={`rounded-xl border overflow-hidden shadow-sm ${config.pinnedBg} ${config.pinnedBorder}`}>
        {/* Thick accent bar */}
        <div className={`h-1.5 ${config.accent}`} />

        <button onClick={onToggle} className="w-full text-left px-4 py-3.5 group">
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${config.badgeBg}`}>
              <TypeIcon className={`w-[18px] h-[18px] ${config.iconColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <TypeBadge config={config} />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Pinned &middot; {formatRelativeDate(announcement.createdDate)}
                </span>
              </div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
                {announcement.title}
              </h4>
              {!isExpanded && (
                <div
                  className={`mt-1 line-clamp-2 ${richContentClasses}`}
                  dangerouslySetInnerHTML={{ __html: announcement.body }}
                />
              )}
            </div>
            <ChevronDownIcon
              className={`w-5 h-5 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform duration-200
                          ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        <Collapsible open={isExpanded}>
          <div className="px-4 pb-4 pl-[3.75rem]">
            <div className="border-t border-gray-200/60 dark:border-gray-700/60 pt-3">
              <div className={richContentClasses}>
                <div dangerouslySetInnerHTML={{ __html: announcement.body }} />
              </div>
            </div>
          </div>
        </Collapsible>
      </div>
    </div>
  );
};

// ============================================
// FeaturedAnnouncement — collapsible hero card
// ============================================

const FeaturedAnnouncement = ({ announcement, isExpanded, onToggle }) => {
  const config = typeConfig[announcement.messageType] || typeConfig.info;
  const TypeIcon = config.icon;
  const scrollRef = useScrollOnExpand(isExpanded);

  return (
    <div ref={scrollRef} className="mx-4 mb-2">
      <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden ${announcement.isRead ? 'opacity-60' : ''}`}>
        {/* Header bar */}
        <button onClick={onToggle} className="w-full text-left px-4 py-3 group">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${config.badgeBg}`}>
              <TypeIcon className={`w-5 h-5 ${config.iconColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
                  {announcement.title}
                </h3>
                {!announcement.isRead && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary-50 dark:bg-primary-900/30 text-primary dark:text-primary-light">
                    New
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <TypeBadge config={config} />
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {formatRelativeDate(announcement.createdDate)}
                </span>
              </div>
              {/* Preview when collapsed */}
              {!isExpanded && (
                <div
                  className={`mt-1.5 line-clamp-3 ${richContentClasses}`}
                  dangerouslySetInnerHTML={{ __html: announcement.body }}
                />
              )}
            </div>
            <ChevronDownIcon
              className={`w-5 h-5 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform duration-200
                          ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {/* Body */}
        <Collapsible open={isExpanded}>
          <div className="px-4 pb-4 pl-[3.5rem]">
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <div
                className={richContentClasses}
                dangerouslySetInnerHTML={{ __html: announcement.body }}
              />
            </div>
          </div>
        </Collapsible>
      </div>
    </div>
  );
};

// ============================================
// AccordionItem — compact row for older announcements
// ============================================

const AccordionItem = ({ announcement, isExpanded, onToggle, isLast }) => {
  const config = typeConfig[announcement.messageType] || typeConfig.info;
  const TypeIcon = config.icon;
  const scrollRef = useScrollOnExpand(isExpanded);

  return (
    <div ref={scrollRef} className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} ${announcement.isRead ? 'opacity-60' : ''}`}>
      <button onClick={onToggle} className="w-full text-left px-3.5 py-2.5 group">
        <div className="flex items-center gap-2.5">
          <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${config.accent}`} />
          {!announcement.isRead && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
          )}
          <TypeIcon className={`w-4 h-4 flex-shrink-0 ${config.iconColor}`} />
          <h4
            className={`text-[13px] min-w-0 flex-1 truncate group-hover:text-primary dark:group-hover:text-primary-light transition-colors
                        ${!announcement.isRead
                          ? 'text-gray-900 dark:text-white font-semibold'
                          : 'text-gray-600 dark:text-gray-300 font-medium'}`}
          >
            {announcement.title}
          </h4>
          <span className="text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
            {formatRelativeDate(announcement.createdDate)}
          </span>
          <ChevronDownIcon
            className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform duration-200
                        ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      <Collapsible open={isExpanded}>
        <div className="px-3.5 pb-3 pl-[2.25rem]">
          <div className="border-t border-gray-100 dark:border-gray-700/60 pt-2.5">
            <div className="flex items-center gap-2 mb-2">
              <TypeBadge config={config} />
              <span className="text-[11px] text-gray-400 dark:text-gray-500">
                {formatRelativeDate(announcement.createdDate)}
              </span>
            </div>
            <div className={richContentClasses}>
              <div dangerouslySetInnerHTML={{ __html: announcement.body }} />
            </div>
          </div>
        </div>
      </Collapsible>
    </div>
  );
};

// ============================================
// SectionLabel
// ============================================

const SectionLabel = ({ label, count }) => (
  <div className="px-5 pt-3 pb-1.5 flex items-center gap-2">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}
    </span>
    {count > 0 && (
      <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500">
        ({count})
      </span>
    )}
  </div>
);

// ============================================
// AnnouncementsButton
// ============================================

const AnnouncementsButton = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const seenIdsRef = useRef(null);

  const { pinned, featured, earlier } = useMemo(() => {
    const p = announcements.filter((a) => a.isPinned);
    const regular = announcements.filter((a) => !a.isPinned);
    return {
      pinned: p,
      featured: regular[0] || null,
      earlier: regular.slice(1),
    };
  }, [announcements]);

  const fetchAnnouncements = useCallback(async () => {
    const result = await getAnnouncements();
    if (result.success) {
      setAnnouncements(result.announcements);
      setUnreadCount(result.unreadCount);
    }
    return result;
  }, []);

  // Fetch on mount + poll, auto-opening for unread announcements.
  // Pauses polling when the tab is hidden.
  useEffect(() => {
    let cancelled = false;

    const load = async (isInitial) => {
      const result = await fetchAnnouncements();
      setLoading(false);
      if (cancelled || !result) return;

      const items = result.announcements || [];

      if (isInitial) {
        // Auto-open once per session on first load if anything is unread.
        seenIdsRef.current = new Set(items.map((a) => a.id));
        if (
          result.unreadCount > 0 &&
          !sessionStorage.getItem(AUTO_OPEN_SESSION_KEY)
        ) {
          sessionStorage.setItem(AUTO_OPEN_SESSION_KEY, 'true');
          setIsOpen(true);
        }
        return;
      }

      // Subsequent polls: open if a genuinely new unread announcement arrived.
      // If the initial load hasn't established a baseline yet (e.g. its request
      // is still pending), seed it from this poll instead of comparing — avoids
      // a null deref and only suppresses auto-open for this one cycle.
      if (!seenIdsRef.current) {
        seenIdsRef.current = new Set(items.map((a) => a.id));
        return;
      }
      const hasNewUnread = items.some(
        (a) => !a.isRead && !seenIdsRef.current.has(a.id)
      );
      seenIdsRef.current = new Set(items.map((a) => a.id));
      if (hasNewUnread) {
        setIsOpen(true);
      }
    };

    load(true);

    const interval = setInterval(() => {
      if (!document.hidden) {
        load(false);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [fetchAnnouncements]);


  const handleToggle = useCallback(
    async (id) => {
      setExpandedIds((prev) => {
        if (prev.has(id)) {
          return new Set();
        }
        return new Set([id]);
      });

      const item = announcements.find((a) => a.id === id);
      if (item && !item.isRead) {
        setAnnouncements((prev) =>
          prev.map((a) => (a.id === id ? { ...a, isRead: true } : a))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
        await markAnnouncementRead(id);
      }
    },
    [announcements]
  );

  const handleMarkAllRead = useCallback(async () => {
    setAnnouncements((prev) =>
      prev.map((a) => ({ ...a, isRead: true }))
    );
    setUnreadCount(0);
    await markAllAnnouncementsRead();
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setExpandedIds(new Set());
  };

  return (
    <>
      {/* Bell Icon Button */}
      <button
        onClick={() => {
          fetchAnnouncements();
          setIsOpen(true);
        }}
        className={`relative ml-0.5 sm:ml-3 p-1 sm:p-2 rounded-lg transition-all active:scale-95
                    focus:outline-none focus:ring-2 focus:ring-primary/30
                    ${unreadCount > 0
                      ? 'text-primary dark:text-primary-light hover:bg-primary-50 dark:hover:bg-primary-900/20'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
        aria-label="View announcements"
      >
        <MegaphoneIcon className="h-4 w-4 sm:h-5 sm:w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 sm:top-0 sm:right-0 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-secondary text-white text-[10px] font-bold px-1 ring-2 ring-white dark:ring-gray-900">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Announcements Modal */}
      <OathModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          <span className="inline-flex items-center gap-2.5">
            Announcements
            {unreadCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary-50 dark:bg-primary-900/30 text-primary dark:text-primary-light">
                {unreadCount} new
              </span>
            )}
          </span>
        }
        size="lg"
        footer={
          <>
            <div className="flex-1" />
            {unreadCount > 0 && (
              <OathButton variant="outline" size="sm" onClick={handleMarkAllRead}>
                <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                Mark all as read
              </OathButton>
            )}
            <OathButton variant="primary" size="sm" onClick={handleClose}>
              Done
            </OathButton>
          </>
        }
      >
        {loading ? (
          <div className="flex justify-center py-12">
            <OathSpinner size="md" />
          </div>
        ) : announcements.length === 0 ? (
          <OathEmptyState
            icon={MegaphoneIcon}
            title="No announcements"
            message="You're all caught up! Check back later for updates."
          />
        ) : (
          <div className="-mx-6 -my-6 bg-gray-50 dark:bg-gray-900 py-2 min-h-[200px]">

            {/* Pinned */}
            {pinned.length > 0 && (
              <>
                <SectionLabel label="Pinned" count={pinned.length} />
                {pinned.map((item) => (
                  <PinnedBanner
                    key={item.id}
                    announcement={item}
                    isExpanded={expandedIds.has(item.id)}
                    onToggle={() => handleToggle(item.id)}
                  />
                ))}
              </>
            )}

            {/* Latest */}
            {featured && (
              <>
                <SectionLabel label="Latest" />
                <FeaturedAnnouncement
                  announcement={featured}
                  isExpanded={expandedIds.has(featured.id)}
                  onToggle={() => handleToggle(featured.id)}
                />
              </>
            )}

            {/* Earlier */}
            {earlier.length > 0 && (
              <>
                <SectionLabel label="Earlier" count={earlier.length} />
                <div className="mx-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {earlier.map((item, idx) => (
                    <AccordionItem
                      key={item.id}
                      announcement={item}
                      isExpanded={expandedIds.has(item.id)}
                      onToggle={() => handleToggle(item.id)}
                      isLast={idx === earlier.length - 1}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Bottom spacing */}
            <div className="h-3" />
          </div>
        )}
      </OathModal>
    </>
  );
};

export default AnnouncementsButton;
